import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';
import { ApiCodeDisplay } from '../ApiCodeDisplay';

interface OnboardingScreenProps {
  screen: Screen;
  onNext?: () => void;
}

export const OnboardingScreen: React.FC<OnboardingScreenProps> = ({ screen, onNext }) => {
  // Note: This component doesn't have access to the full screens array
  // For now, we'll use a simple approach
  
  const [formData, setFormData] = useState(
    screen.form?.fields.reduce((acc, field) => {
      // Convert date format from DD/MM/YYYY to YYYY-MM-DD for date input
      if (field.name === 'birth_date' && field.value) {
        const dateParts = field.value.split('/');
        if (dateParts.length === 3) {
          acc[field.name] = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
        } else {
          acc[field.name] = field.value;
        }
      } else {
        acc[field.name] = field.value;
      }
      return acc;
    }, {} as Record<string, string>) || {}
  );

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Form */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            <div className="p-6 h-full flex flex-col">
              <div className="text-center mb-6">
                <h2 className="text-sm text-gray-600 mb-1">
                  {screen.form?.title}
                </h2>
                <h3 className="text-xl font-bold text-black">
                  {screen.form?.subtitle}
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  {screen.form?.instruction}
                </p>
              </div>

              <div className="flex-1 space-y-2">
                {/* First Name and Last Name - Side by Side */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0 * 0.1 }}
                  className="flex space-x-2"
                >
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData['first_name'] || ''}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData['last_name'] || ''}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>

                {/* Email - Full Width */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1 * 0.1 }}
                >
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData['email'] || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </motion.div>

                {/* Birth Date - Full Width */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 2 * 0.1 }}
                >
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Birth Date
                  </label>
                  <input
                    type="date"
                    value={formData['birth_date'] || ''}
                    onChange={(e) => handleInputChange('birth_date', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </motion.div>

                {/* Address - Full Width */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 3 * 0.1 }}
                >
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData['address'] || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </motion.div>

                {/* City and Country - Side by Side */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 4 * 0.1 }}
                  className="flex space-x-2"
                >
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData['city'] || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={formData['country'] || ''}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onNext}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold mt-6 cursor-pointer"
              >
                Submit
              </motion.button>
            </div>
          </PhoneFrame>
        </motion.div>

        {/* Right Side - API Code */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {screen.description}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              {screen.steps?.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex items-center space-x-2">
                    <div className={`step-dot ${step.status}`} />
                    <span className={`text-sm font-medium ${
                      step.status === 'active' ? 'text-blue-600' : 
                      step.status === 'completed' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < (screen.steps?.length || 0) - 1 && (
                    <div className="w-8 h-px bg-gray-300" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-2">
                <div className="text-sm font-medium text-gray-600">
                  &lt;/&gt; Code
                </div>
              </div>
            </div>

            <ApiCodeDisplay apiCode={screen.apiCode} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
